import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Endpoint, EndpointService } from '@services/endpoints';
import { IRole } from '@services/users';
import { Observable } from 'rxjs';

import { I18nService } from '../../i18n/services';
import { IClustersServiceData, ICountry, IGender, IClusterLocales } from './clusters.models';

@Injectable({
	providedIn: 'root'
})
export class ClustersService {
	public data: IClustersServiceData = {
		locales: [],
		genders: [],
		countries: [],
		roles: []
	};

	constructor(private endpointService: EndpointService, private http: HttpClient, private i18nService: I18nService) {}

	public clearCache() {
		this.data.locales = [];
		this.data.genders = [];
		this.data.countries = [];
		this.data.roles = [];
	}

	public getRoles(force = false): Observable<IRole[]> {
		const url = `${this.endpointService.url(Endpoint.usersRolesAll)}`;

		return new Observable<IRole[]>((observer) => {
			if (force || !this.data.roles || this.data.roles.length === 0) {
				this.http.get<Array<IRole & { id?: string; description?: string }>>(url).subscribe(
					(res: Array<IRole & { id?: string; description?: string }>) => {
						this.data.roles = [...res]
							.map((role) => ({
								roleId: role.roleId || role.id,
								desc: role.desc || role.description || role.roleId || role.id
							}))
							.filter((role) => !!role.roleId)
							.sort((a, b) => a.roleId.localeCompare(b.roleId, undefined, { sensitivity: 'base' }));
						observer.next(this.data.roles);
						observer.complete();
					},
					(error: any) => {
						observer.error(error);
						observer.complete();
					}
				);
			} else {
				observer.next(this.data.roles);
				observer.complete();
			}
		});
	}

	public getClusterLocales(force = false): Observable<IClusterLocales[]> {
		const url = `${this.endpointService.url(Endpoint.usersLocales)}`;

		return new Observable<IClusterLocales[]>((observer) => {
			if (force || !this.data.locales || this.data.locales.length === 0) {
				this.http.get<IClusterLocales[]>(url).subscribe(
					(res: IClusterLocales[]) => {
						this.data.locales = res;
						observer.next(this.data.locales);
						observer.complete();
					},
					(error: any) => {
						observer.error(error);
						observer.complete();
					}
				);
			} else {
				observer.next(this.data.locales);
				observer.complete();
			}
		});
	}

	public getClusterGenders(force = false): Observable<IGender[]> {
		const url = `${this.endpointService.url(Endpoint.usersGenders)}`;

		return new Observable<IGender[]>((observer) => {
			if (force || !this.data.genders || this.data.genders.length === 0) {
				this.http.get<string[]>(url).subscribe(
					(res: string[]) => {
						this.data.genders = res.map((gender) => {
							return {
								id: gender,
								label: this.i18nService.get(gender, `app.gender.${gender}`)
							};
						});

						observer.next(this.data.genders);
						observer.complete();
					},
					(error: any) => {
						observer.error(error);
						observer.complete();
					}
				);
			} else {
				observer.next(this.data.genders);
				observer.complete();
			}
		});
	}

	public getClusterCountries(force = false): Observable<ICountry[]> {
		const url = `${this.endpointService.url(Endpoint.usersCountries)}`;

		return new Observable<ICountry[]>((observer) => {
			if (force || !this.data.countries || this.data.countries.length === 0) {
				this.http.get<string[]>(url).subscribe(
					(res: string[]) => {
						this.data.countries = res
							.map((country) => {
								return {
									id: country,
									label: this.i18nService.get(country, `app.countryCode.${country.toLowerCase()}`)
								};
							})
							.sort((a, b) => {
								return a.label.localeCompare(b.label);
							});

						observer.next(this.data.countries);
						observer.complete();
					},
					(error: any) => {
						observer.error(error);
						observer.complete();
					}
				);
			} else {
				observer.next(this.data.countries);
				observer.complete();
			}
		});
	}
}
